import { computed, effect, Injectable, signal, Signal } from "@angular/core";
import { AbstractControl, FormControl, FormGroup } from "@angular/forms";
import { toSignal } from "@angular/core/rxjs-interop";

type ControlValue<C extends AbstractControl<any>> =
  C extends import("@angular/forms").FormControl<infer V> ? V : unknown;

type FormSignals<T extends Record<string, AbstractControl>> = {
  [K in keyof T]: Signal<ControlValue<T[K]>>;
};

@Injectable({
  providedIn: "root",
})
export class SignalUtils {
  constructor() {}

  static debouncedSignal<T>(
    sourceSignal: Signal<T>,
    debounceTimeInMs = 0,
  ): Signal<T> {
    const debounced = signal(sourceSignal());
    let isFirstRun = true;

    effect(
      (onCleanup) => {
        const value = sourceSignal();
        if (isFirstRun) {
          isFirstRun = false;
          debounced.set(value);
          return;
        }
        const timeout = setTimeout(
          () => debounced.set(value),
          debounceTimeInMs,
        );
        onCleanup(() => clearTimeout(timeout));
      },
      { allowSignalWrites: true },
    );
    return debounced;
  }

  static formStatus(fc: AbstractControl): SignalFormStatus {
    if (!fc) {
      return null;
    }
    const touchedReactive: Signal<boolean> = fc["touchedReactive"];
    const pristineReactive: Signal<boolean> = fc["pristineReactive"];
    const validReactive = computed(() => fc["statusReactive"]() === "VALID");
    return {
      touched: touchedReactive,
      pristine: pristineReactive,
      valid: validReactive,
    };
  }

  static formToSignal<T extends Record<string, AbstractControl>>(
    formGroup: FormGroup<T>,
  ): FormSignals<T> {
    const result = {} as FormSignals<T>;

    (Object.keys(formGroup.controls) as (keyof T)[]).forEach((key: string) => {
      const ctrl = formGroup.controls[key];
      (result as any)[key] = toSignal(ctrl.valueChanges, {
        initialValue: ctrl.value,
      });
    });
    return result;
  }

  static controlToSignal<T>(control: FormControl<T>): Signal<T> {
    return toSignal(control.valueChanges, {
      initialValue: control.value as T,
    });
  }
}

export interface SignalFormStatus {
  touched: Signal<boolean>;
  pristine: Signal<boolean>;
  valid: Signal<boolean>;
}
